import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMeCore } from "../../../cores/me";
import { useEmailMarketingCore } from "../../../cores/emailMarketing";
import { useRouterCore } from "../../../cores/router";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import { EmailComposer } from "./EmailComposer";
import { DEFAULT_RICH_HTML, wrapRichHtmlForEmail } from "./emailHtmlUtils";
import "./EmailMarketing.scss";

export const EmailMarketingTemplatesScreen = () => {
  const intl = useIntl();
  const { company } = useMeCore();
  const { goToEmailMarketingEmailsFromTemplate } = useRouterCore();
  const {
    templates: apiTemplates,
    loadTemplates,
    createTemplate,
  } = useEmailMarketingCore();
  const [filter, setFilter] = useState<"all" | "dragDrop" | "html" | "plain">("all");
  const [composerOpen, setComposerOpen] = useState(false);
  const [templateSubject, setTemplateSubject] = useState(
    intl.formatMessage({ id: "app.emailMarketing.templates.defaultName" })
  );
  const [templateHtml, setTemplateHtml] = useState(DEFAULT_RICH_HTML);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company?.id) {
      loadTemplates(company.id);
    }
  }, [company?.id, loadTemplates]);

  const sourceTemplates = apiTemplates;

  const visibleTemplates =
    filter === "all"
      ? sourceTemplates
      : sourceTemplates.filter((template) => template.type === filter);

  const openWizardFromTemplate = (templateId: string) => {
    goToEmailMarketingEmailsFromTemplate(templateId);
  };

  const openNewTemplate = () => {
    setTemplateSubject(
      intl.formatMessage({ id: "app.emailMarketing.templates.defaultName" })
    );
    setTemplateHtml(DEFAULT_RICH_HTML);
    setComposerOpen(true);
  };

  const saveTemplate = async () => {
    if (!company?.id) return;
    setSaving(true);
    const saved = await createTemplate(company.id, {
      name: templateSubject,
      subject: templateSubject,
      html: wrapRichHtmlForEmail(templateHtml),
      editor: "code",
    });
    setSaving(false);
    if (saved) {
      setComposerOpen(false);
      await loadTemplates(company.id);
    }
  };

  return (
    <Layout>
      <Content className="email-mkt email-mkt--templates">
        <section className="email-mkt__hero">
          <div className="email-mkt__hero-content">
            <div>
              <span className="email-mkt__eyebrow">
                <FormattedMessage id="app.sidebar.emailMarketing" />
              </span>
              <h1 className="email-mkt__title">
                <FormattedMessage id="app.emailMarketing.templates.title" />
              </h1>
              <p className="email-mkt__subtitle">
                <FormattedMessage id="app.emailMarketing.templates.subtitle" />
              </p>
            </div>
            <div className="email-mkt__stats">
              <div className="email-mkt__stat">
                <strong>{sourceTemplates.length}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.templates.stats.total" />
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="email-mkt__panel">
          <div className="email-mkt__toolbar email-mkt__toolbar--split">
            <div className="email-mkt__segmented">
              <button
                type="button"
                className={filter === "all" ? "is-active" : ""}
                onClick={() => setFilter("all")}
              >
                <FormattedMessage id="app.emailMarketing.emails.tabs.all" />
              </button>
              <button
                type="button"
                className={filter === "dragDrop" ? "is-active" : ""}
                onClick={() => setFilter("dragDrop")}
              >
                <FormattedMessage id="app.emailMarketing.emails.create.dragDrop" />
              </button>
              <button
                type="button"
                className={filter === "html" ? "is-active" : ""}
                onClick={() => setFilter("html")}
              >
                <FormattedMessage id="app.emailMarketing.emails.create.html" />
              </button>
              <button
                type="button"
                className={filter === "plain" ? "is-active" : ""}
                onClick={() => setFilter("plain")}
              >
                <FormattedMessage id="app.emailMarketing.emails.create.plainText" />
              </button>
            </div>
            <button
              type="button"
              className="email-mkt__primary-btn"
              onClick={openNewTemplate}
            >
              <FormattedMessage id="app.emailMarketing.templates.create" />
            </button>
          </div>

          {visibleTemplates.length === 0 ? (
            <p className="email-mkt__note">
              <FormattedMessage id="app.emailMarketing.templates.empty" />
            </p>
          ) : (
          <div className="email-mkt__template-grid">
            {visibleTemplates.map((template) => (
              <article className="email-mkt__template-card" key={template.id}>
                <div
                  className="email-mkt__template-thumb"
                  style={{ borderColor: template.accent }}
                >
                  <span style={{ background: template.accent }} />
                </div>
                <div>
                  <strong>{template.name}</strong>
                  <small>{template.updatedAt}</small>
                </div>
                <button
                  type="button"
                  className="email-mkt__ghost-btn"
                  onClick={() => openWizardFromTemplate(template.id)}
                >
                  <FormattedMessage id="app.emailMarketing.templates.use" />
                </button>
              </article>
            ))}
          </div>
          )}
        </section>
        <EmailComposer
          open={composerOpen}
          subject={templateSubject}
          html={templateHtml}
          initialMode="rich"
          saving={saving}
          onSubjectChange={setTemplateSubject}
          onHtmlChange={setTemplateHtml}
          onClose={() => setComposerOpen(false)}
          onSaveDraft={saveTemplate}
          saveLabelId="app.emailMarketing.templates.save"
        />
      </Content>
    </Layout>
  );
};
